import React, { useCallback, useMemo } from 'react'

import { ApolloError } from '@apollo/client'
import clsx from 'clsx'

import ToDoCheckbox from '$extensionSrc/components/ToDoCheckbox'
import CheckmarkIcon from '$extensionSrc/icons/checkmark-icon.svg'
import EmailIcon from '$extensionSrc/icons/email-icon.svg'
import {
  EmailCategorySettingUpdate,
  EmailSetting,
  EmailSingleSettingUpdate,
} from '$extensionSrc/notifications/notificationTypes'

import styles from './NotificationCategorySettings.module.scss'
import NotificationSettingRow from './NotificationSettingRow'

enum UPDATE_STAGE {
  SAVING = 'SAVING',
  SAVED = 'SAVED',
}

export default function NotificationCategorySettings({
  category,
  categoryBeingSaved,
  onCategoryChannelSettingUpdate,
  onSingleSettingUpdate,
  settings,
  updateError,
}: {
  category: string
  categoryBeingSaved: string | null
  onCategoryChannelSettingUpdate: (ccsu: EmailCategorySettingUpdate) => void
  onSingleSettingUpdate: (ssu: EmailSingleSettingUpdate) => void
  settings: EmailSetting[]
  updateError?: ApolloError
}) {
  const totalSettings = useMemo(() => settings.length, [settings])
  const enabledSettings = useMemo(
    () =>
      settings.reduce(
        (acc, current) => acc + (current.settingValue ? 1 : 0),
        0,
      ),
    [settings],
  )

  const handleCategoryChannelCheckboxClick = useCallback(() => {
    const targetValue = enabledSettings !== totalSettings
    onCategoryChannelSettingUpdate({
      category,
      targetValue,
    })
  }, [totalSettings, enabledSettings, onCategoryChannelSettingUpdate, category])

  const [updateStage, setUpdateStage] = React.useState<UPDATE_STAGE | null>(
    null,
  )
  const timeoutRef = React.useRef<number | undefined>(undefined)
  React.useEffect(() => {
    if (!categoryBeingSaved && updateStage === UPDATE_STAGE.SAVING) {
      if (!updateError) {
        // Transition from saving -> saved
        window.clearTimeout(timeoutRef.current)
        setUpdateStage(UPDATE_STAGE.SAVED)
        timeoutRef.current = window.setTimeout(() => {
          // Transition from saved -> none
          setUpdateStage(null)
        }, 1000)
      } else {
        // Transition from saving -> none (error)
        setUpdateStage(null)
      }
    } else if (categoryBeingSaved === category && updateStage === null) {
      // Transition from none -> saving
      setUpdateStage(UPDATE_STAGE.SAVING)
    }
  }, [category, categoryBeingSaved, updateStage, updateError])
  React.useEffect(
    () => () => {
      window.clearTimeout(timeoutRef.current)
    },
    [],
  )

  return (
    <div className={clsx(styles.NotificationCategorySettings)}>
      <div className={clsx(styles.settingsSectionHeader, 'heading3', 'bold')}>
        <div className={clsx(styles.notificationHeadingCategory)}>
          {category}
          {updateStage && (
            <span className={clsx(styles.settingsHeaderStatus, 'darkest-blue')}>
              {updateStage === UPDATE_STAGE.SAVING && 'Saving...'}
              {updateStage === UPDATE_STAGE.SAVED && (
                <>
                  Saved
                  <CheckmarkIcon
                    className={clsx(styles.settingsHeaderSuccessIcon)}
                  />
                </>
              )}
            </span>
          )}
        </div>
        <div
          className={clsx(styles.notificationHeadingChannels, 'small-label1')}
        >
          <div className={clsx(styles.notificationChannelLabel)}>
            <EmailIcon aria-hidden className={clsx(styles.channelIcon)} /> Email
          </div>
        </div>
      </div>
      <div className={clsx(styles.settingsSectionBody)}>
        <div className={clsx(styles.notificationSettings)}>
          {settings.map((setting) => (
            <NotificationSettingRow
              category={setting.category}
              description={setting.description}
              key={setting.id}
              name={setting.name}
              onSingleSettingUpdate={onSingleSettingUpdate}
              settingId={setting.id}
              settingValue={setting.settingValue}
            />
          ))}
        </div>
        <div className={clsx(styles.notificationSettingsFooter)}>
          <div className={clsx(styles.settingsFooterCategory, 'body2', 'bold')}>
            All {category}
          </div>
          <div className={clsx(styles.settingsFooterChannels)}>
            <div className={clsx(styles.notificationCategoryChannelCheckbox)}>
              <ToDoCheckbox
                isIndeterminate={
                  enabledSettings > 0 && enabledSettings < totalSettings
                }
                isSettingSelected={enabledSettings === totalSettings}
                labelId={`${category}`}
                onClick={handleCategoryChannelCheckboxClick}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
